import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaGoogle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { authService, analyticsService } from '../../services';
import { toast } from 'react-hot-toast';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

// Extend the Window interface to include Google types
declare global {
  interface Window {
    google: {
      accounts?: {
        id: {
          initialize: (config: any) => void;
          prompt: (callback?: () => void) => void;
          renderButton: (element: HTMLElement, options: any) => void;
        };
        oauth2?: {
          initTokenClient: any;
        };
      };
      // Include any existing properties like maps
      maps?: any;
    };
  }
}

// Google client ID is imported from authService

interface GoogleAuthButtonProps {
  mode: 'login' | 'register';
  returnTo: string;
  className?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({
  mode,
  returnTo,
  className = '',
  onSuccess,
  onError
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { shouldUseRedirect, isIOSSafari } = useDeviceDetection();

  const handleGoogleAuth = async () => {
    setLoading(true);
    
    try {
      // Use redirect method directly for iOS Safari and other problematic browsers
      if (shouldUseRedirect) {
        console.log('Using redirect method for', isIOSSafari ? 'iOS Safari' : 'mobile browser');
        await handleGoogleAuthRedirect();
        return;
      }

      // Check if Google Sign-In API is available
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        // Try to load Google Sign-In API if not already loaded
        await authService.initGoogleAuth();
        
        // Check again after attempting to load
        if (!window.google || !window.google.accounts || !window.google.accounts.id) {
          console.log('Google Sign-In API could not be loaded. Falling back to redirect method.');
          
          // Fallback to redirect method instead of popup
          await handleGoogleAuthRedirect();
          return;
        }
      }
      
      // Get Google token with improved error handling
      const tokenResponse = await new Promise<{credential?: string}>((resolve, reject) => {
        // Set a timeout to detect if the prompt doesn't appear
        const timeout = setTimeout(() => {
          reject(new Error('Google Sign-In timed out. Falling back to redirect method.'));
        }, 3000);
        
        // Configure a callback function
        const handleCredentialResponse = (response: {credential?: string}) => {
          clearTimeout(timeout);
          resolve(response);
        };
        
        // Initialize Google Sign-In with FedCM support
        window.google.accounts?.id.initialize({
          client_id: window.google.accounts?.oauth2?.initTokenClient 
            ? window.google.accounts?.oauth2?.initTokenClient.client_id 
            : '797792678018-71st865l3ephv4edn6j6hng3qkpcl4le.apps.googleusercontent.com',
          callback: handleCredentialResponse,
          // Enable FedCM
          use_fedcm_for_prompt: true,
          // These settings are compatible with FedCM
          cancel_on_tap_outside: true,
          itp_support: true,
          context: mode === 'login' ? 'signin' : 'signup'
        });
        
        // Show the Google Sign-In prompt
        try {
          window.google.accounts?.id.prompt();
        } catch (promptError: unknown) {
          clearTimeout(timeout);
          const errorMessage = promptError instanceof Error ? promptError.message : 'Unknown error';
          reject(new Error(`Failed to show Google Sign-In prompt: ${errorMessage}`));
        }
      });
      
      // Login or register with the Google token
      if (tokenResponse.credential) {
        let authResponse;
        if (mode === 'login') {
          authResponse = await authService.loginWithGoogle(tokenResponse.credential);
        } else {
          // For registration, we use the registerWithGoogle method which auto-verifies email
          authResponse = await authService.registerWithGoogle(tokenResponse.credential);
          
          // Track Google account creation for registration
          try {
            analyticsService.trackAccountCreation('google', authResponse.user?.id);
          } catch (analyticsError) {
            console.error('Error tracking Google account creation:', analyticsError);
            // Don't block the flow if analytics tracking fails
          }
        }
        
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(returnTo);
        }
      } else {
        throw new Error('No credential received from Google');
      }
    } catch (err: any) {
      console.error(`Google ${mode} error:`, err);
      
      // Try fallback method if there's a sign-in issue
      try {
        console.log('Falling back to OAuth redirect method');
        await handleGoogleAuthRedirect();
        return;
      } catch (fallbackErr: any) {
        console.error(`Google ${mode} fallback error:`, fallbackErr);
        const errorMessage = fallbackErr.message || 'Please try again later.';
        
        if (onError) {
          onError(new Error(errorMessage));
        } else {
          const errorKey = mode === 'login' ? 'login.googleLoginFailed' : 'register.googleRegisterFailed';
          toast.error(t(errorKey) + ' ' + errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Redirect method for iOS Safari and mobile browsers
  const handleGoogleAuthRedirect = async () => {
    // Get the OAuth URL from authService
    const authUrl = await authService.getGoogleOAuthUrl(returnTo, mode === 'register');
    
    // Navigate directly to the OAuth URL (redirect)
    window.location.href = authUrl;
  };

  // Fallback method using traditional OAuth popup
  const handleGoogleAuthFallback = async () => {
    // Get the OAuth URL from authService
    const authUrl = await authService.getGoogleOAuthUrl(returnTo, mode === 'register');
    
    // Use a popup window to avoid navigating away
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popup = window.open(
      authUrl,
      'Google Sign-In',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
    );
    
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      throw new Error('Popup blocked. Please enable popups for this website.');
    }
    
    // Return a promise that resolves when the popup completes
    return new Promise((resolve, reject) => {
      // Set up message listener for the popup result
      const messageHandler = (event: MessageEvent) => {
        // Validate the origin
        if (event.origin !== window.location.origin) {
          return;
        }
        
        // Check for auth data
        if (event.data && event.data.type === 'googleAuth') {
          window.removeEventListener('message', messageHandler);
          
          if (event.data.error) {
            reject(new Error(event.data.error));
            return;
          }
          
          if (event.data.token) {
            // Store the token in localStorage
            localStorage.setItem('authToken', event.data.token);
            localStorage.setItem('lastActivityTime', Date.now().toString());
            resolve(event.data);
            
            // Close the popup if it's still open
            if (popup && !popup.closed) {
              popup.close();
            }
            
            // Navigate to the target page instead of just refreshing
            if (event.data.needsRefresh) {
              if (onSuccess) {
                onSuccess();
              } else {
                navigate(returnTo);
              }
            }
          }
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      // Check popup status periodically
      const popupTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupTimer);
          window.removeEventListener('message', messageHandler);
          reject(new Error('Authentication was cancelled.'));
        }
      }, 500);
      
      // Set a timeout
      setTimeout(() => {
        clearInterval(popupTimer);
        window.removeEventListener('message', messageHandler);
        if (!popup.closed) {
          popup.close();
        }
        reject(new Error('Authentication timed out.'));
      }, 120000); // 2 minute timeout
    });
  };

  const buttonText = loading 
    ? shouldUseRedirect 
      ? t('auth.googleAuth.redirecting')
      : mode === 'login' ? t('login.signingIn') : t('register.signingUp')
    : mode === 'login' ? t('login.continueWithGoogle') : t('register.continueWithGoogle');

  return (
    <button
      onClick={handleGoogleAuth}
      disabled={loading}
      className={`w-full flex items-center justify-center gap-3 h-[60px] rounded-[16px] font-medium text-black bg-white border border-gray-300 ${className}`}
    >
      <FaGoogle className="text-blue-600" />
      {buttonText}
    </button>
  );
};

export default GoogleAuthButton; 